import { Input, StyledText } from 'UI';
import { MarkIcon } from 'assets/icons';
import { AxiosError } from 'axios';
import Loader from 'components/Loader';
import { useAsyncAction } from 'hooks';
import { FC, useRef, useState } from 'react';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import styled, { css, useTheme } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import { validations } from 'utils';

type AuthorizedInputProps = {
  setIsAuthorized: (value: boolean) => void;
  courseId: string;
};

const AuthorizedInput: FC<AuthorizedInputProps> = ({ setIsAuthorized, courseId }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { colors } = useTheme();

  const dispatch = useAppDispatch();

  const [email, setEmail] = useState('');

  const [error, setError] = useState('');

  const direction = useAppSelector(selectors.direction.selectDirection);
  const directionId = direction?.id || '';

  const [getCustomerByEmail] = useAsyncAction(actions.stripe.getCustomerByEmail);

  const [emailForError, setEmailForError] = useState('');
  const [isExistingCustomer, setIsExistingCustomer] = useState(false);
  const [customerIdLoading, setCustomerIdLoading] = useState(false);

  const getCustomer = async (email: string, directionId: string) => {
    console.log('📧 [AUTH-INPUT] Getting customer for email:', email);
    setCustomerIdLoading(true);
    setIsExistingCustomer(false);

    try {
      const { isExistingCustomer } = await getCustomerByEmail({ email, directionId });
      console.log('📧 [AUTH-INPUT] Customer check result:', { email, isExistingCustomer });

      if (isExistingCustomer) {
        console.log('🔴 [AUTH-INPUT] Existing customer - blocking authorization');
        setIsExistingCustomer(isExistingCustomer);
        setEmailForError(email);
        setCustomerIdLoading(false);
        setIsAuthorized(false);
      }

      if (!isExistingCustomer) {
        console.log('✅ [AUTH-INPUT] New customer - setting isAuthorized to TRUE');
        setIsExistingCustomer(isExistingCustomer);
        setCustomerIdLoading(false);
        setIsAuthorized(true);

        dispatch(actions.auth.setEmail(email));
      }
    } catch (error) {
      console.error('🔴 [AUTH-INPUT] Error checking customer:', error);
      setCustomerIdLoading(false);
      setError((error as AxiosError).message);
      setIsAuthorized(false);
    }
  };

  const signUpByEmail = async () => {
    console.log('📧 [AUTH-INPUT] signUpByEmail called with:', { email, directionId });
    const validationError = validations.email(email);

    if (validationError) {
      console.log('🔴 [AUTH-INPUT] Email validation failed:', validationError);
      setError(validationError);
      return;
    } else {
      setError('');
    }

    if (directionId) {
      console.log('✅ [AUTH-INPUT] Email valid, checking customer...');
      await getCustomer(email, directionId);
    } else {
      console.log('🔴 [AUTH-INPUT] No directionId available');
    }
  };

  const handleOnChange = (e: any) => {
    setEmail(e.target.value);
    setError('');
  };

  const handleKeyUp = (e: any) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }
  };

  return (
    <>
      <Input
        ref={inputRef}
        placeholder="Email"
        value={email}
        onChange={handleOnChange}
        onKeyUp={handleKeyUp}
        onBlur={signUpByEmail}
        disabled={customerIdLoading}
        label="Email"
        showIcon={Boolean(!isExistingCustomer)}
        iconComponent={
          customerIdLoading ? <Loader width={18} height={18} /> : <MarkIcon color={colors.system.green} size={18} />
        }
        maxLength={155}
      />
      <ErrorText>
        {isExistingCustomer ? (
          <div style={{ display: 'flex' }}>
            <p>
              We noticed that you already have a trial account with {emailForError}. Please
              <a
                style={{ textDecoration: 'underline', marginLeft: '3px', marginRight: '3px', fontWeight: 'bold' }}
                href={`https://study.traineracademy.org/sign-in?isLandingPage=true&courseId=${courseId}`}
                // href={`http://localhost:3000/sign-in?isLandingPage=true&courseId=${courseId}`}
                target="_blank"
                rel="noreferrer">
                log in here to purchase.
              </a>
            </p>
          </div>
        ) : (
          error
        )}
      </ErrorText>
    </>
  );
};

export default AuthorizedInput;

const ErrorText = styled(StyledText)`
  text-align: start;
  margin-bottom: 16px;

  ${({ theme: { colors, typography } }) => css`
    color: ${colors.system.red};
    ${typography.body_basic_medium_14}

    ${respondToWidth.s`
      ${typography.footnote_semibold_12}
    `}
  `};
`;
